import { useCallback, useEffect, useState, useRef, useContext, useMemo } from "react";
import { Toast } from "primereact/toast";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { Paginator } from "primereact/paginator";
import { BreadCrumb } from "primereact/breadcrumb";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { ProgressSpinner } from "primereact/progressspinner";
import { Menu } from "primereact/menu";
import ProductForm from "./ProductForm";
import { getProducts, createProduct, updateProduct, toggleProduct, deleteProducts } from "../../api/productApi";
import { AuthContext } from "../../Context/AuthContext";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

export default function Product() {
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState([]);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(10);
  const [formVisible, setFormVisible] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [filterValue, setFilterValue] = useState("");
  const [uploadVisible, setUploadVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const toast = useRef(null);
  const menu = useRef(null);
  const { auth } = useContext(AuthContext);
  const token = auth?.token;

  const items = [{ label: "PRODUCT" }];
  const home = { icon: "pi pi-home", url: "/" };

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await getProducts(token);
      setProducts(res.data.data || []);
    } catch {
      toast.current?.show({ severity: "error", summary: "Error", detail: "Failed to fetch products" });
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) fetchData();
  }, [token, fetchData]);

  const sentenceCase = str => str ? str.toLowerCase().replace(/(^\s*\w|[.!?]\s*\w)/g, c => c.toUpperCase()) : "";

  const textEditor = options => (
    <InputText
      value={options.value}
      onChange={e => options.editorCallback(e.target.value)}
      disabled={!options.rowData.is_enabled}
    />
  );

  const onCellEditComplete = e => {
    const { rowData, newValue } = e;
    if (!rowData.is_enabled) {
      toast.current?.show({ severity: "warn", summary: "Forbidden", detail: "Disabled product cannot be edited" });
      return;
    }
    if (!newValue || newValue === rowData.name) return;

    confirmDialog({
      message: "Are you sure you want to update this product?",
      header: "Confirm Update",
      icon: "pi pi-exclamation-triangle",
      accept: async () => {
        setIsLoading(true);
        try {
          const res = await updateProduct(rowData.id, { name: sentenceCase(newValue) }, token);
          setProducts(prev => prev.map(p => (p.id === rowData.id ? res.data.data : p)));
          toast.current?.show({ severity: "success", summary: "Updated", detail: "Product updated" });
        } catch (err) {
          const msg = err?.response?.data?.message || "Update failed";
          toast.current?.show({ severity: "error", summary: "Error", detail: msg });
        } finally {
          setIsLoading(false);
        }
      }
    });
  };

  const handleDisable = productIds => {
    if (!productIds.length) return;
    confirmDialog({
      message: "Are you sure you want to toggle status of selected product(s)?",
      header: "Confirm Status Toggle",
      icon: "pi pi-exclamation-triangle",
      accept: async () => {
        setIsLoading(true);
        try {
          const updated = await Promise.all(productIds.map(async id => {
            const res = await toggleProduct(id, token);
            return res.data.data;
          }));
          setProducts(prev => prev.map(p => updated.find(u => u.id === p.id) || p));
          setSelected([]);
          toast.current?.show({ severity: "success", summary: "Updated", detail: "Status updated" });
        } catch {
          toast.current?.show({ severity: "error", summary: "Error", detail: "Status update failed" });
        } finally {
          setIsLoading(false);
        }
      }
    });
  };

  const handleDelete = (productIds) => {
  if (!productIds.length) return;

  confirmDialog({
    message: "Are you sure you want to delete the selected product(s)?",
    header: "Confirm Delete",
    icon: "pi pi-exclamation-triangle",
    accept: async () => {
      setIsLoading(true);
      try {
        await deleteProducts(productIds, token);

        await fetchData();
        setSelected([]);

        toast.current?.show({
          severity: "success",
          summary: "Deleted",
          detail: "Products deleted successfully"
        });

      } catch (err) {
        const msg =
          err?.response?.data?.message ||
          "Delete failed. No products were deleted.";

        toast.current?.show({
          severity: "error",
          summary: "Deletion Failed",
          detail: msg
        });
      } finally {
        setIsLoading(false);
      }
    }
  });
};

  const rowClassName = rowData => ({
    "opacity-50": !rowData.is_enabled,
    "cursor-not-allowed": !rowData.is_enabled
  });

  const handleFormSubmit = async (formData) => {
    setIsLoading(true);
    try {
      const payload = { name: sentenceCase(formData.name) };
      if (editingProduct) await updateProduct(editingProduct.id, payload, token);
      else await createProduct(payload, token);
      setFormVisible(false);
      setEditingProduct(null);
      fetchData();
      toast.current?.show({ severity: "success", summary: "Success", detail: "Operation completed" });
    } catch (err) {
      const msg = err?.response?.data?.message || "Operation failed";
      toast.current?.show({ severity: "error", summary: "Error", detail: msg });
    } finally {
      setIsLoading(false);
    }
  };

  const filteredProducts = useMemo(() => {
    if (!filterValue) return products;
    return products.filter(p => p.name.toLowerCase().includes(filterValue.toLowerCase()));
  }, [products, filterValue]);

  const filterEnabledProducts = data => data.filter(p => p.is_enabled);

  const downloadJSON = () => {
    if (selected.some(p => !p.is_enabled)) {
      toast.current?.show({ severity: "warn", summary: "Action Forbidden", detail: "Disabled products cannot be downloaded." });
      return;
    }
    const data = selected.length ? selected : filterEnabledProducts(products);
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "products.json";
    link.click();
  };

  const downloadExcel = () => {
    if (selected.some(p => !p.is_enabled)) {
      toast.current?.show({ severity: "warn", summary: "Action Forbidden", detail: "Disabled products cannot be downloaded." });
      return;
    }
    const data = selected.length ? selected : filterEnabledProducts(products);
    const worksheet = XLSX.utils.json_to_sheet(data.map(p => ({ name: p.name })));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Products");
    XLSX.writeFile(workbook, "products.xlsx");
  };

  const downloadPDF = () => {
    if (selected.some(s => !s.is_enabled)) {
      toast.current?.show({ 
        severity: "warn", 
        summary: "Action Forbidden", 
        detail: "Disabled products cannot be downloaded." 
      });
      return;
    }
    const data = selected.length ? selected : filterEnabledProducts(products);
    const doc = new jsPDF();
    doc.setFontSize(18);
    doc.text("Products", 14, 20);
    autoTable(doc, { 
      head: [["Name"]], 
      body: data.map(d => [d.name]), 
      startY: 30 
    });
    doc.save("products.pdf");
  };

  const downloadTemplateJSON = () => {
    const template = [{ name: "Product 1", source: "Source 1" }];
    const blob = new Blob([JSON.stringify(template, null, 2)], { type: "application/json" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "product_upload_template.json";
    link.click();
  };

  const downloadTemplateExcel = () => {
    const template = [{ name: "Product 1", source: "Source 1" }];
    setTimeout(() => {
      const worksheet = XLSX.utils.json_to_sheet(template);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Template");
      XLSX.writeFile(workbook, "product_upload_template.xlsx");
    }, 0);
  };

  const menuItems = [
    { label: "Download JSON Template", icon: "pi pi-file", command: downloadTemplateJSON },
    { label: "Download Excel Template", icon: "pi pi-file-excel", command: downloadTemplateExcel }
  ];

  return (
    <div className="p-4" style={{ position: 'relative' }}>
      {isLoading && (
        <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(255,255,255,0.8)', zIndex: 10, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <ProgressSpinner />
        </div>
      )}
      <Toast ref={toast} />
      <ConfirmDialog />

      <h2 className="mb-1 text-3xl font-bold" style={{ color: "#FC5F1CFF" }}>PRODUCT</h2>
      <BreadCrumb model={items} home={home} style={{ background: "transparent", border: "none" }} />

      <DataTable
        value={filteredProducts.slice(first, first + rows)}
        dataKey="id"
        selection={selected}
        onSelectionChange={e => setSelected(e.value)}
        editMode="cell"
        showGridlines
        rowClassName={rowClassName}
        header={
          <div className="flex justify-content-end gap-2">
            <Button 
              icon="pi pi-plus" 
              rounded 
              tooltip="add"
              onClick={() => setFormVisible(true)} 
              style={{ background: "#FC5F1CFF", border: "none" }} 
            />
            <Button 
              icon="pi pi-upload" 
              rounded 
              tooltip="upload"
              onClick={() => setUploadVisible(true)} 
              style={{ background: "#FC5F1CFF", border: "none" }} 
            />
            <Button 
              icon="pi pi-file-pdf" 
              rounded 
              tooltip="PDF" 
              onClick={downloadPDF} 
              style={{ background: "#FC5F1CFF", border: "none" }} 
            />
            <Button 
              icon="pi pi-file" 
              rounded 
              tooltip="json"
              onClick={downloadJSON} 
              style={{ background: "#FC5F1CFF", border: "none" }} 
            />
            <Button 
              icon="pi pi-file-excel" 
              rounded 
              tooltip="xlsx"
              onClick={downloadExcel} 
              style={{ background: "#FC5F1CFF", border: "none" }} 
            />
            <Button
              // icon={selected.some(p => p.is_enabled) ? "pi pi-ban" : "pi pi-check"}
              icon="pi pi-ban" 
              rounded
              tooltip="disable"
              disabled={!selected.length}
              onClick={() => handleDisable(selected.map(p => p.id))}
              style={{ background: "#FC5F1CFF", border: "none" }}
            />
            <Button
              icon="pi pi-trash"
              rounded
              tooltip="delete"
              disabled={!selected.length}
              onClick={() => handleDelete(selected.map(p => p.id))}
              style={{ background: "#FF4B4B", border: "none" }}
            />
            <Menu model={menuItems} popup ref={menu} />
            <Button 
              icon="pi pi-ellipsis-v" 
              rounded 
              tooltip="Template"
              style={{ background: "#FC5F1CFF", border: "none" }} 
              onClick={e => menu.current.toggle(e)} 
            />
          </div>
        }
        footer={
          <Paginator 
            first={first} 
            rows={rows} 
            rowsPerPageOptions={[10, 20, 30]}
            totalRecords={filteredProducts.length} 
            onPageChange={e => { setFirst(e.first); setRows(e.rows); }} 
          />
        }
      >
        <Column selectionMode="multiple" style={{ width: "3rem" }} />
        <Column field="name" header="Product Name" editor={textEditor} onCellEditComplete={onCellEditComplete} />
      </DataTable>

      <ProductForm
        visible={formVisible}
        onHide={() => { setFormVisible(false); setEditingProduct(null); }}
        onSubmit={handleFormSubmit}
        initialData={editingProduct}
      />

      <ProductForm
        visible={uploadVisible}
        uploadMode
        onHide={() => setUploadVisible(false)}
        onSubmit={async (data) => {
          confirmDialog({
            message: "Are you sure you want to upload these products?",
            header: "Confirm Upload",
            icon: "pi pi-exclamation-triangle",
            accept: async () => {
              setIsLoading(true);
              try {
                for (const p of data) {
                  const payload = { 
                    name: p.name ? p.name.trim().replace(/\s+/g, ' ').replace(/(^\w)|(\s\w)/g, c => c.toUpperCase()) : "",
                    source: p.source || null
                  };
                  await createProduct(payload, token);
                }
                setUploadVisible(false);
                fetchData();
                toast.current?.show({ severity: "success", summary: "Uploaded", detail: "Products uploaded successfully" });
              } catch (err) {
                toast.current?.show({ severity: "error", summary: "Error", detail: err.message });
              } finally {
                setIsLoading(false);
              }
            }
          });
        }}
      />
    </div>
  );
}
